import { images } from '@/asset';
import BaseModal from '@/components/BaseModal';
import BoldSmallText from '@/components/BoldSmallText';
import CollapsibleBanner from '@/components/CollapsibleBanner';
import CustomCard from '@/components/customCard';
import CustomGridLayoutPreview from '@/components/CustomGridLayoutPreview';
import BaseImage from '@/components/Icons/BaseImage';
import Layout from '@/components/layout';
import ModalConfirmLeavePage from '@/components/ModalConfirmLeavePage';
import PreviewVideo from '@/components/PreviewVideo';
import RegularText from '@/components/RegularText';
import WarningBanner from '@/components/WarningBanner';
import { BREAKPOINT, Touchpoint } from '@/constants/enum';
import { optionLanguage } from '@/constants/options';
import { PATH, USER_GUIDE_LINK } from '@/constants/path';
import { checkoutExtensionInfo } from '@/constants/string';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import useGenerateCheckoutEditorLink from '@/hooks/useGenerateCheckoutEditorLink';
import useToast from '@/hooks/useToast';
import useTrackTouchpoint from '@/hooks/useTrackTouchpoint';
import useUserPlan from '@/hooks/useUserPlan';
import CustomizeAppearanceCard from '@/pages/Checkout/components/CustomizeAppearanceCard';
import Table from '@/pages/Checkout/components/Table';
import { CheckoutStyled } from '@/pages/Checkout/styled';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import bannerSlice, { bannerSelector } from '@/redux/slice/banner.slice';
import sessionSlice, { isShowBannerSelector } from '@/redux/slice/session.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import toastSlice from '@/redux/slice/toast.slice';
import { IParamsApi } from '@/types/apis/params';
import { Badge, Button, ChoiceList, Icon, Link, List, Tooltip } from '@shopify/polaris';
import { ArrowRightIcon, ExternalIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

interface IProps {}

export default function Checkout(props: IProps) {
  useCustomUserIdClarity('ETA checkout extension');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { show: showToast } = useToast();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  const [updateGeneralSetting, { isLoading: isUpdatingSetting }] = apiCaller.useUpdateGeneralSettingMutation();
  const { isEnableCheckout, isActivePlusPlan } = useUserPlan();
  const dataSettings = useSelector(dataSettingsSelector);
  const bannerState = useSelector(isShowBannerSelector);
  const { isVisibleBannerCheckoutExt: isVisibleBannerInfo } = useSelector(bannerSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const trackTouchpoint = useTrackTouchpoint();

  const { data: checkoutRes } = apiCaller.useGetCheckoutRulesQuery(
    { page: 1, perPage: 5, shippingMethods: '' },
    { skip: isSkip },
  );
  const { data: dataAppearance, isLoading: isLoadingAppearance } = apiCaller.useGetCheckoutAppearanceQuery(undefined, {
    skip: isSkip,
  });
  const [update, { isLoading: isLoadingUpdateAppearance }] = apiCaller.useUpdateCheckoutAppearanceMutation();
  const isEmptyCheckoutMethod = checkoutRes?.data?.length === 0;
  const checkoutEditorInfo = useGenerateCheckoutEditorLink();

  const [modalGuide, setModalGuide] = useState({ open: false, type: 'checkout' });
  const [openModalConfirmLeave, setOpenModalConfirmLeave] = useState(false);
  const [showModalPriority, setShowModalPriority] = useState(false);
  const [conditionShowCheckoutRule, setConditionShowCheckoutRule] = useState<IParamsApi.IConditionShowCheckoutRule | undefined>(
    dataSettings.conditionShowCheckoutRule,
  );

  useEffect(() => {
    setConditionShowCheckoutRule(dataSettings?.conditionShowCheckoutRule);
  }, [dataSettings?.conditionShowCheckoutRule]);

  const isEnableCartLine = useMemo(() => {
    if (dataAppearance?.data) return !!dataAppearance?.data?.isEnableCartLine;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dataAppearance)]);

  const handleUpdateAppearance = async () => {
    if (!dataAppearance?.data || isSkip) return;
    try {
      const res = await update({ ...dataAppearance?.data, isEnableCartLine: !isEnableCartLine }).unwrap();
      if (res.code === 200) dispatch(toastSlice.actions.handleToast({ content: 'Saved', isOpen: true }));
      else dispatch(toastSlice.actions.handleToast({ content: 'Something wrong', isOpen: true }));
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleSavePriority = async () => {
    if (!conditionShowCheckoutRule?.length) return;
    try {
      const res = await updateGeneralSetting({
        ...dataSettings,
        conditionShowCheckoutRule,
      }).unwrap();
      if (res.status === 'success') {
        showToast('Saved');
      }
      setShowModalPriority(false);
    } catch (error) {}
  };

  const handleCloseModalPriority = () => {
    setShowModalPriority(false);
    setOpenModalConfirmLeave(false);
    setConditionShowCheckoutRule(dataSettings?.conditionShowCheckoutRule);
  };

  const handleChangePriority = (selected: IParamsApi.IConditionShowCheckoutRule[]) => {
    setConditionShowCheckoutRule(selected[0]);
  };

  const handleClickCloseModal = () => {
    if (dataSettings?.conditionShowCheckoutRule !== conditionShowCheckoutRule) setOpenModalConfirmLeave(true);
    else handleCloseModalPriority();
  };

  const handleRemoveBannerLimit = () => {
    dispatch(sessionSlice.actions.handleBanner({ key: 'isVisibleBannerLimitCheckout', value: false }));
  };

  return (
    <CheckoutStyled>
      <Layout
        fullWidth={false}
        titleMetadata={<Badge tone="info">Only for Shopify Plus</Badge>}
        title="ETA checkout extension"
        subtitle="Show delivery estimates on Checkout, Thank you, and Order status pages."
      >
        {isEnableCheckout ? null : isActivePlusPlan ? (
          <WarningBanner
            isVisible={bannerState.isVisibleBannerLimitCheckout}
            hideContact
            title="ETA checkout extension is only available for Plus plan"
            onDismiss={handleRemoveBannerLimit}
          >
            <RegularText>
              Enhance checkout experience and reduce abandonment by helping customers evaluate whether ETA is suitable and worth
              the cost.
            </RegularText>
            <div className="mt-8">
              <Button
                variant="primary"
                onClick={() => {
                  navigate(PATH.PRICING_PLAN);
                  trackTouchpoint(Touchpoint.UpgradeETACheckout);
                }}
              >
                Upgrade now
              </Button>
            </div>
          </WarningBanner>
        ) : (
          <WarningBanner
            isVisible={bannerState.isVisibleBannerLimitCheckout}
            hideContact
            title="Shopify plan eligibility"
            onDismiss={handleRemoveBannerLimit}
          >
            <RegularText>
              This feature is only available for <BoldSmallText>Shopify Plus</BoldSmallText> merchants.{' '}
            </RegularText>
          </WarningBanner>
        )}

        {/* <div className="mb-12">
          <Feedback />
        </div> */}

        <div className="mb-12">
          <PreviewVideo
            primaryOnClick={() => window.open(USER_GUIDE_LINK.CHECKOUT_RULE_SETUP, '_blank')}
            title="ETA Checkout Extension"
            description="Show dynamic delivery estimates on Checkout shipping method, Thank you, and Order status pages."
            videoLength={225}
            thumbnailUrl={images.thumbnailCheckoutExtension}
            videoUrl="https://www.youtube.com/embed/9tnZXKsH3uE"
          />
        </div>

        {isVisibleBannerInfo ? (
          <div style={{ marginTop: isEnableCheckout ? 0 : '-4px' }}>
            <CollapsibleBanner
              isVisible={isVisibleBannerInfo}
              bannerTitle="Why ETA checkout extension is a perfect complementary to original Rule setup for Shopify Plus?"
              bannerContent={checkoutExtensionInfo.map((item) => (
                <div key={item.id} className="mt-8">
                  <p>{item.title}</p>
                  <div className="ml-16">
                    <List type="bullet">
                      {item.content.map((content, index) => (
                        <List.Item key={index}>{content}</List.Item>
                      ))}
                    </List>
                  </div>
                  <p className="mt-8">
                    ETA Checkout Extension complements the original Rule setup by enhancing the checkout experience. While the
                    original setup allows for custom ETAs on storefront (product, collection page, etc.), ETA checkout extension
                    focuses specifically on checkout flows. This seamless integration ensures that customers always have peace of
                    mind about their order delivery times.
                  </p>
                </div>
              ))}
              collapsible={true}
              onClose={() => {
                dispatch(bannerSlice.actions.handleVisibleBanner({ key: 'isVisibleBannerCheckoutExt', value: false }));
              }}
            />
          </div>
        ) : null}

        <div className={!isEnableCheckout ? 'disable' : ''}>
          <div className="mt-12">
            <CustomCard
              title={
                <div className={`d-flex gap-8 ${isMobile ? 'flex-column' : 'align-center'}`}>
                  <BoldSmallText>Checkout - Shipping method</BoldSmallText>
                  <div className="cursor-pointer" onClick={() => navigate(PATH.TRANSLATION)}>
                    <Tooltip content={'Default language'}>
                      <Badge tone="info">
                        {
                          (
                            <div style={{ fontSize: '0.75rem' }}>
                              {optionLanguage.find((item) => item.value === dataSettings.date_locale)?.label || ''}
                            </div>
                          ) as any
                        }
                      </Badge>
                    </Tooltip>
                  </div>
                </div>
              }
              textClassName="ot-polaris-text-18"
            >
              <CustomGridLayoutPreview>
                <div className="d-flex flex-column mb-8">
                  <div className="mt-8">
                    <BoldSmallText>Add dynamic delivery estimates for shipping methods on the checkout page.</BoldSmallText>
                  </div>
                  <div className="mt-8">
                    <p>Follow these 2 steps:</p>
                    <List type="number">
                      <List.Item>Create delivery estimate rules for shipping methods.</List.Item>
                      <List.Item>
                        Add an app block in checkout.{' '}
                        <Button
                          variant="plain"
                          onClick={() => {
                            setModalGuide({ open: true, type: 'checkout' });
                          }}
                        >
                          See guide
                        </Button>
                      </List.Item>
                    </List>
                  </div>
                  <div className="d-flex mt-8 gap-8">
                    <Button
                      disabled={!isEnableCheckout}
                      onClick={() => navigate(PATH.CREATE_CHECKOUT_EXTENSION)}
                      variant="primary"
                    >
                      Create a rule
                    </Button>

                    {checkoutEditorInfo.isHasId ? (
                      <Button
                        icon={ExternalIcon}
                        disabled={!isEnableCheckout || isEmptyCheckoutMethod}
                        onClick={() => {
                          window.open(checkoutEditorInfo.link);
                        }}
                      >
                        Add block
                      </Button>
                    ) : null}
                  </div>
                  <div className="mt-8">
                    <Button variant="plain" onClick={() => setShowModalPriority(true)}>
                      Checkout rule priority
                    </Button>
                  </div>
                </div>
                <div style={{ maxHeight: '170px' }}>
                  <BaseImage style={{ objectFit: 'contain', height: '170px' }} src={images.demoPreviewCheckout} alt="preview" />
                </div>
              </CustomGridLayoutPreview>

              <Table />
            </CustomCard>
          </div>

          <div className="mt-12">
            <CustomizeAppearanceCard
              title={'Checkout - Product line items'}
              isEmptyCheckoutMethod={isEmptyCheckoutMethod}
              imgSrc={images.previewProductLineItems}
              badgeHeader={<Badge tone={isEnableCartLine ? 'success' : 'critical'}>{isEnableCartLine ? 'On' : 'Off'}</Badge>}
            >
              <div className="d-flex flex-column mb-16">
                <div className="mt-8">
                  <BoldSmallText>Show selected shipping method with delivery estimates in each product line item</BoldSmallText>
                </div>
                <div className="mt-8">
                  <p>To display product line items, follow these steps:</p>
                  <List type="number">
                    <List.Item>Create a rule for shipping methods and add a block in checkout.</List.Item>
                    <List.Item>Enable the checkout product line items feature.</List.Item>
                  </List>
                </div>
                <div className="mt-12">
                  <div className="d-flex gap-8">
                    <Button
                      disabled={!isEnableCheckout || isEmptyCheckoutMethod || isLoadingAppearance || isLoadingUpdateAppearance}
                      loading={isLoadingAppearance || isLoadingUpdateAppearance}
                      onClick={() => {
                        handleUpdateAppearance();
                      }}
                      variant={isEnableCartLine ? undefined : 'primary'}
                    >
                      {isEnableCartLine ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      disabled={isEmptyCheckoutMethod || !isEnableCheckout}
                      onClick={() => navigate(PATH.EDIT_CHECKOUT_PRODUCT_LINE_ITEMS)}
                    >
                      Customize
                    </Button>
                  </div>
                </div>
              </div>
            </CustomizeAppearanceCard>
          </div>

          <div className="mt-12">
            <CustomizeAppearanceCard
              title={'Thank you and order status page'}
              isEmptyCheckoutMethod={isEmptyCheckoutMethod}
              imgSrc={images.demoPreviewThankyouOrder}
            >
              <div className="d-flex flex-column mb-16">
                <div className="mt-8">
                  <BoldSmallText>Show shipping method with delivery estimates selected in Checkout</BoldSmallText>
                </div>
                <div className="mt-8">
                  <p>To display the banner, follow these steps:</p>
                  <List type="number">
                    <List.Item>Create a rule for shipping methods and add a block in checkout.</List.Item>
                    <List.Item>
                      Add an app block to the Thank You and order status pages.{' '}
                      <Button
                        variant="plain"
                        onClick={() => {
                          setModalGuide({ open: true, type: 'thankyou' });
                        }}
                      >
                        See guide
                      </Button>
                    </List.Item>
                  </List>
                </div>
                <div className="mt-12">
                  <div className="d-flex gap-8">
                    {checkoutEditorInfo.isHasId ? (
                      <Button
                        icon={ExternalIcon}
                        disabled={!isEnableCheckout || isEmptyCheckoutMethod}
                        onClick={() => {
                          window.open(checkoutEditorInfo.linkThankYou);
                        }}
                        variant="primary"
                      >
                        Add block
                      </Button>
                    ) : null}
                    <Button
                      disabled={isEmptyCheckoutMethod || !isEnableCheckout}
                      onClick={() => navigate(PATH.EDIT_CHECKOUT_APPEARANCE)}
                    >
                      Edit appearance
                    </Button>
                  </div>
                </div>
              </div>
            </CustomizeAppearanceCard>
          </div>
        </div>

        <BaseModal
          open={modalGuide.open}
          onClose={() => setModalGuide({ open: false, type: 'checkout' })}
          title="How to add app block in Checkout editor?"
          primaryAction={{
            content: 'Got it',
            onAction: () => setModalGuide({ open: false, type: 'checkout' }),
          }}
        >
          <List type="number">
            <List.Item>
              <p className="d-flex gap-4">
                Open{' '}
                <Link
                  url={modalGuide.type === 'checkout' ? checkoutEditorInfo.link : checkoutEditorInfo.linkThankYou}
                  target="_blank"
                  monochrome
                >
                  <div className="d-flex gap-4">
                    Checkout editor <Icon source={ArrowRightIcon} />
                  </div>
                </Link>
              </p>
            </List.Item>
            <List.Item>
              Select <b>Apps</b> in vertical bar. Search <strong>Omega Estimated Delivery Date</strong>
            </List.Item>
            <List.Item>Add block to desired page</List.Item>
          </List>
          <div className="mt-8 text-center">
            <img width={'100%'} src={images.checkoutBlockGuide} alt="checkout block guide" />
          </div>
        </BaseModal>

        <BaseModal
          open={!openModalConfirmLeave && showModalPriority}
          onClose={handleClickCloseModal}
          title="Checkout rule priority"
          primaryAction={{
            content: 'Save',
            onAction: handleSavePriority,
            loading: isUpdatingSetting,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: handleClickCloseModal,
              loading: isUpdatingSetting,
            },
          ]}
        >
          <ChoiceList
            title="As only 1 rule can be displayed for a shipping method, when multiple rules are applicable for a shipping method, the system will prioritize and return a single rule based on:"
            choices={[
              { label: 'Earliest minimum preparation date', value: IParamsApi.IConditionShowCheckoutRule.EarliestMinPrepare },
              { label: 'Longest minimum preparation date', value: IParamsApi.IConditionShowCheckoutRule.LongestMinPrepare },
              { label: 'Earliest maximum preparation date', value: IParamsApi.IConditionShowCheckoutRule.EarliestMaxPrepare },
              { label: 'Longest maximum preparation date', value: IParamsApi.IConditionShowCheckoutRule.LongestMaxPrepare },
              { label: 'Earliest minimum delivery date', value: IParamsApi.IConditionShowCheckoutRule.EarliestMinDelivery },
              { label: 'Longest minimum delivery date', value: IParamsApi.IConditionShowCheckoutRule.LongestMinDelivery },
              { label: 'Earliest maximum delivery date', value: IParamsApi.IConditionShowCheckoutRule.EarliestMaxDelivery },
              { label: 'Longest maximum delivery date', value: IParamsApi.IConditionShowCheckoutRule.LongestMaxDelivery },
            ]}
            selected={[conditionShowCheckoutRule as IParamsApi.IConditionShowCheckoutRule]}
            onChange={handleChangePriority}
          />
        </BaseModal>
      </Layout>
      <ModalConfirmLeavePage
        open={openModalConfirmLeave}
        onClose={() => setOpenModalConfirmLeave(false)}
        onConfirm={handleCloseModalPriority}
        confirmButtonText="Confirm"
      />
    </CheckoutStyled>
  );
}
